<div mat-dialog-title class="dialog-title p-3 pr-5">
  <h5>{{ isEditMode ? 'Edit System Banner' : 'Create System Banner' }}</h5>
  <button
    mat-icon-button
    aria-label="Close dialog"
    matTooltip="Close this dialog."
    type="button"
    (click)="onClose()"
    class="dialog-close-button">
    <mat-icon>close</mat-icon>
  </button>
</div>

<form [formGroup]="form" (ngSubmit)="onSave()">

  <hr class="m-0 p-0">

  <mat-dialog-content class="px-3 py-4">

    <!-- Content (TinyMCE) -->
    <div class="row">
      <div class="col-12">
        <label for="banner_content" class="form-label d-block mb-2">
          <b>Content</b>
          <span class="text-danger"> *</span>
        </label>
        <div class="system-banner-editor mb-1">
          <app-rich-text-editor
            id="banner_content"
            formControlName="content"
            [height]="250"
            [buttons]="['bold', 'italic', 'underline', '|', 'link']">
          </app-rich-text-editor>
        </div>
        <small class="d-block text-muted mb-4">
          Formatting is restricted to bold, italic, underline and links.
          Press Enter to start a new paragraph or Shift+Enter for a single
          line break. Any other markup is stripped on save.
        </small>
      </div>
    </div>

    <!-- Style + Dismissible -->
    <div class="row">
      <div class="col-12 col-md-6">
        <mat-form-field class="full-width mb-4" appearance="outline" subscriptSizing="dynamic">
          <mat-label>Style</mat-label>
          <mat-select formControlName="style" required>
            <mat-option *ngFor="let option of styleOptions" [value]="option.value">
              {{ option.label }}
            </mat-option>
          </mat-select>
          <mat-hint>Controls the visual tone of the banner.</mat-hint>
        </mat-form-field>
      </div>
      <div class="col-12 col-md-6 d-flex align-items-center">
        <mat-checkbox formControlName="is_dismissible" class="mb-4">
          Users can dismiss this banner
        </mat-checkbox>
      </div>
    </div>

    <!-- Platforms -->
    <div class="row">
      <div class="col-12">
        <label class="form-label d-block mb-2">
          <b>Platforms</b>
          <span class="text-danger"> *</span>
        </label>
        <div class="d-flex flex-wrap align-items-center mb-2">
          <mat-checkbox formControlName="show_web" class="mr-4">Show on Web</mat-checkbox>
          <mat-checkbox formControlName="show_mobile" class="mr-4">Show on Mobile</mat-checkbox>
          <mat-checkbox formControlName="show_kiosk">Show on Kiosk</mat-checkbox>
        </div>
        <div class="text-danger mb-2" *ngIf="platformsInvalid">
          <small>At least one platform must be enabled.</small>
        </div>
        <div class="alert alert-warning mb-4" role="alert" *ngIf="showSmallScreenWarning">
          <mat-icon class="align-middle mr-2">info</mat-icon>
          <small class="align-middle">
            Mobile and kiosk screens have limited space for banners. Keep
            content short and to the point. Long messages may push other
            content below the fold or wrap onto many lines.
          </small>
        </div>
      </div>
    </div>

    <!-- Account Targeting -->
    <div class="row">
      <div class="col-12">
        <label class="form-label d-block mb-2">
          <b>Target Accounts (optional)</b>
        </label>
        <div class="d-flex flex-wrap align-items-center mb-2">
          <button
            type="button"
            mat-raised-button
            color="secondary"
            class="mr-2 mb-2"
            (click)="onSelectAccounts()">
            <mat-icon>business</mat-icon>
            Select Accounts
          </button>
          <button
            type="button"
            mat-stroked-button
            class="mb-2"
            *ngIf="selectedAccounts.length > 0"
            (click)="onClearAccounts()">
            Clear All
          </button>
        </div>
        <mat-chip-listbox
          *ngIf="selectedAccounts.length > 0"
          class="mb-2"
          aria-label="Selected accounts">
          <mat-chip
            *ngFor="let account of selectedAccounts"
            (removed)="onRemoveAccount(account)">
            {{ account.name }}
            <button matChipRemove type="button" [attr.aria-label]="'Remove ' + account.name">
              <mat-icon>cancel</mat-icon>
            </button>
          </mat-chip>
        </mat-chip-listbox>
        <small class="d-block text-muted mb-4">
          <ng-container *ngIf="selectedAccounts.length === 0">
            Broadcast to all accounts. Click "Select Accounts" to limit this
            banner to specific organisations.
          </ng-container>
          <ng-container *ngIf="selectedAccounts.length > 0">
            This banner is targeted to {{ selectedAccounts.length }}
            {{ selectedAccounts.length === 1 ? 'account' : 'accounts' }}.
          </ng-container>
        </small>
        <div class="mb-4">
          <mat-checkbox formControlName="include_network">
            Include connected network of selected accounts
          </mat-checkbox>
          <small class="d-block text-muted mt-1">
            When enabled, the banner is also shown to every contractor and
            subcontractor of the selected target accounts (to the nth level).
            The connected network is cached when you save. If the network
            changes over time (new contractors added, existing ones removed),
            save the banner again to resync the audience.
          </small>
        </div>
      </div>
    </div>

    <!-- Scheduling -->
    <div class="row">
      <div class="col-12">
        <label class="form-label d-block mb-2">
          <b>Schedule (optional)</b>
        </label>
        <small class="d-block text-muted mb-3">
          Choose the timezone you want the start and end times to apply in.
          Banners are shown to all users globally once the start time is
          reached, regardless of where they are.
        </small>
      </div>
      <div class="col-12">
        <app-timezone-select
          [timezoneInput]="selectedTimezone"
          (timezoneOutput)="getOutputTimezone($event)"></app-timezone-select>
      </div>
      <div class="col-12 col-md-6">
        <app-datetime-picker
          [selectedDateTime]="startsAtDateTime"
          [pickerName]="'starts_at'"
          [label]="'Starts At (optional)'"
          [hint]="'Leave blank to start immediately on publish.'"
          [classes]="'full-width mb-4'"
          (pickerDetailsOutput)="onStartsAtChanged($event)"></app-datetime-picker>
      </div>
      <div class="col-12 col-md-6">
        <app-datetime-picker
          [selectedDateTime]="endsAtDateTime"
          [pickerName]="'ends_at'"
          [label]="'Ends At (optional)'"
          [hint]="'Leave blank for no end date.'"
          [classes]="'full-width mb-4'"
          (pickerDetailsOutput)="onEndsAtChanged($event)"></app-datetime-picker>
      </div>
      <div class="col-12">
        <div class="text-danger mb-3" *ngIf="scheduleRangeInvalid">
          <small>Starts At must be before Ends At.</small>
        </div>
      </div>
    </div>

  </mat-dialog-content>

  <hr class="m-0 p-0">

  <mat-dialog-actions class="d-flex justify-content-end px-3 py-2">
    <button
      mat-raised-button
      type="button"
      class="my-2 mr-2"
      (click)="onClose()">
      Cancel
    </button>
    <button
      mat-raised-button
      type="submit"
      class="my-2"
      color="primary"
      [disabled]="cannotSave">
      <mat-icon>save</mat-icon> {{ isEditMode ? 'Save Changes' : 'Create Banner' }}
    </button>
  </mat-dialog-actions>

</form>
